import { sdk } from "./medusa"

/** The delivery person's money (backend lib/hq/courier.ts, api/admin/hq/courier). */
export type CourierMethod = "meetup" | "home"
export type CourierDelivery = {
  key: string
  source: "site" | "custom"
  ref: string
  order_id: string | null
  order_number: string | null
  method: CourierMethod
  delivered_at: string
  customer: string | null
  cash: number
}
export type CourierLine = CourierDelivery & { pay: number }
export type CourierSettings = { rates: { meetup: number; home: number }; since: string; excluded: string[] }
export type CourierSettlement = {
  id: string
  settled_at: string
  by: string | null
  meetups: number
  homes: number
  rate_meetup: number
  rate_home: number
  pay_total: number
  cash_expected: number
  kept: number
  owed: number
  expected_hand_over: number
  cash_handed: number
  short: number
  note: string | null
  lines: CourierLine[]
  excluded: CourierDelivery[]
  latest: boolean
}
export type CourierPayment = { id: string; amount: number; paid_at: string; by: string | null; note: string | null }
export type CourierPage = {
  settings: CourierSettings
  open: { since: string; lines: CourierLine[]; left_out: CourierDelivery[]; meetups: number; homes: number; pay: number; cash: number }
  owed: number
  settlements: CourierSettlement[]
  payments: CourierPayment[]
  preview: Record<"keep" | "owe", { kept: number; owed: number; expected_hand_over: number }>
}

const post = <T>(path: string, body: Record<string, unknown>) => sdk.client.fetch<T>(path, { method: "POST", body })

export const courierApi = {
  page: () => sdk.client.fetch<CourierPage>("/admin/hq/courier"),
  settle: (body: { request_id: string; keys: string[]; cash_handed: number; keep_pay: boolean; note: string | null }) =>
    post<{ id: string; replay: boolean; cash_marked: number; cash_failed: Array<{ ref: string; error: string }> }>("/admin/hq/courier/settle", body),
  pay: (body: { request_id: string; amount: number; note: string | null }) => post<{ id: string }>("/admin/hq/courier/payments", body),
  deletePayment: (id: string) => post<{ id: string }>(`/admin/hq/courier/payments/${encodeURIComponent(id)}/delete`, {}),
  undo: (id: string) => post<{ id: string }>(`/admin/hq/courier/settlements/${encodeURIComponent(id)}/undo`, {}),
  settings: (body: { rate_meetup?: number; rate_home?: number; since?: string; exclude?: { key: string; excluded: boolean } }) => post<{ settings: CourierSettings }>("/admin/hq/courier/settings", body),
}

/** Where an order of his opens in HQ. */
export const orderPath = (d: Pick<CourierDelivery, "source" | "order_id" | "order_number">) => (d.source === "site" ? `/orders/${d.order_id}` : `/custom/${d.order_number}`)

/** A date input's value (Athens day) for an instant, and back: the start of that day in Athens. */
export function athensDay(iso: string) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso))
}
export function athensMidnight(day: string) {
  // Athens is UTC+2 or +3: try both and keep the one that is midnight there.
  for (const offset of [3, 2]) {
    const candidate = new Date(`${day}T00:00:00+0${offset}:00`)
    if (athensDay(candidate.toISOString()) === day && new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Athens", hour: "2-digit", hourCycle: "h23" }).format(candidate) === "00") return candidate.toISOString()
  }
  return new Date(`${day}T00:00:00Z`).toISOString()
}
